import { useApplyPromoCode, useRemovePromoCode } from '@massilia/queries'
import { AxiosError } from 'axios'
import { useState } from 'react'
import { z } from 'zod'

/** Corps d'erreur rédigé par l'API : seul son `message` est affichable. */
const promoCodeErrorBodySchema = z.object({ message: z.string() })

/**
 * Promo-code interaction shared by the informations (step 2) and checkout pages.
 * The discount itself lives on the session (server is source of truth); this hook
 * only owns the transient error message and the apply/remove calls.
 */
export function usePromoCode(sessionId: string) {
  const [promoCodeError, setPromoCodeError] = useState<null | string>(null)

  const { isPending: isVerifying, mutateAsync: applyPromoCode } = useApplyPromoCode(sessionId)
  const { mutateAsync: removePromoCodeMutation } = useRemovePromoCode(sessionId)

  const verify = async (code: string) => {
    if (!code.trim()) return
    setPromoCodeError(null)
    try {
      await applyPromoCode({ code: code.trim() })
    } catch (error) {
      const fallback = 'Code promo invalide.'
      const body =
        error instanceof AxiosError
          ? promoCodeErrorBodySchema.safeParse(error.response?.data)
          : undefined
      setPromoCodeError(body?.success === true ? body.data.message : fallback)
    }
  }

  const remove = async () => {
    setPromoCodeError(null)
    await removePromoCodeMutation()
  }

  return { isVerifying, promoCodeError, remove, verify }
}
